import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { quotesService, } from "@/api/services/quotes.service";
import { quoteRequestsService } from "@/api/services/quote-requests.service";
import { sitesService } from "@/api/services/sites.service";
import { contactsService } from "@/api/services/contacts.service";
import { UploadDocumentsModal } from "@/features/admin/contacts/components/UploadDocumentsModal";
import { useToast, CommencementDateInput, DateInput } from "@/components/common";
import { documentsService } from "@/api/services/documents.service";
import { formatDateOnlyDisplay, isWeekend } from "@/utils/dateTime";
import { searchSuburbsAsync } from "@/data/australian-suburbs";
import styles from "./ClientQuoteNew.module.css";
const TABS = [
    { id: "site_particulars", label: "Site Particulars" },
    { id: "job_description", label: "Job Description" },
    { id: "documents", label: "Documents" },
];
const JOB_DESCRIPTION_FILTERS = [
    { id: "all", label: "All" },
    { id: "permits", label: "Permits" },
    { id: "plans", label: "Plans" },
    { id: "other", label: "Other" },
];
const formatDocDate = (value) => formatDateOnlyDisplay(value);
const getJobDescriptionFilterLabel = (id) => JOB_DESCRIPTION_FILTERS.find((filter) => filter.id === id)?.label ?? id;
const AUSTRALIAN_STATES = [
    { value: "NSW", label: "NSW" },
    { value: "VIC", label: "VIC" },
    { value: "QLD", label: "QLD" },
    { value: "SA", label: "SA" },
    { value: "WA", label: "WA" },
    { value: "TAS", label: "TAS" },
    { value: "NT", label: "NT" },
    { value: "ACT", label: "ACT" },
];
const BUILDING_TYPES = [
    { value: "COMMERCIAL", label: "Commercial" },
    { value: "RETAIL", label: "Retail" },
    { value: "HOSPITALITY", label: "Hospitality" },
    { value: "INDUSTRIAL", label: "Industrial" },
];
export const ClientQuoteNew = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { quoteId } = useParams();
    const { success: showSuccess, error: showError } = useToast();
    // Get copied quote from location state (when copying a quote)
    const copiedQuote = routerLocation.state
        ?.copiedQuote;
    const [activeTab, setActiveTab] = useState("site_particulars");
    const [currentQuoteRequestId, setCurrentQuoteRequestId] = useState(null);
    const isSavingRef = useRef(false);
    const autoSaveTimeoutRef = useRef(null);
    const [defitType, setDefitType] = useState("full");
    const [siteInspectionAvailable, setSiteInspectionAvailable] = useState(true);
    const [permitRequired, setPermitRequired] = useState(true);
    const [state, setState] = useState("");
    const [buildingType, setBuildingType] = useState("");
    const [suburb, setSuburb] = useState("");
    const [postcode, setPostcode] = useState("");
    // Suburb dropdown state
    const [suburbSearchTerm, setSuburbSearchTerm] = useState("");
    const [showSuburbDropdown, setShowSuburbDropdown] = useState(false);
    const [filteredSuburbs, setFilteredSuburbs] = useState([]);
    const suburbDropdownRef = useRef(null);
    // Job Description states
    const [commencementDate, setCommencementDate] = useState("");
    const [commencementTime, setCommencementTime] = useState("");
    const [completionDate, setCompletionDate] = useState("");
    const [completionTime, setCompletionTime] = useState("");
    const [projectDuration, setProjectDuration] = useState("");
    const [budgetOption, setBudgetOption] = useState(null);
    const [customBudget, setCustomBudget] = useState("");
    const [jobDescriptionFilter, setJobDescriptionFilter] = useState("all");
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [isSavingDocuments, setIsSavingDocuments] = useState(false);
    const [isUploadingToBlob, setIsUploadingToBlob] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [pendingDocuments, setPendingDocuments] = useState([]);
    // Site Particulars controlled state
    const [location, setLocation] = useState("");
    const [quoteName, setQuoteName] = useState("");
    const [siteName, setSiteName] = useState("");
    const [tenancyName, setTenancyName] = useState("");
    const [tenancyNumber, setTenancyNumber] = useState("");
    const [squareMeterage, setSquareMeterage] = useState("");
    const [scopeOfWorks, setScopeOfWorks] = useState("");
    // Load existing quote or quote request on mount
    useEffect(() => {
        const loadData = async () => {
            if (!user?.id) {
                // Wait for user to be available
                return;
            }
            try {
                if (quoteId) {
                    // Editing existing quote - load quote and get quoteRequestId
                    const quoteResponse = await quotesService.getQuote(quoteId);
                    const quote = quoteResponse.data;
                    // If quote has quoteRequestId, load the request
                    if (quote.quoteRequestId) {
                        setCurrentQuoteRequestId(quote.quoteRequestId);
                        const request = await quoteRequestsService.getQuoteRequest(quote.quoteRequestId);
                        // Populate form fields from request
                        if (request.location)
                            setLocation(request.location);
                        if (request.suburb) {
                            setSuburb(request.suburb);
                            setSuburbSearchTerm(request.suburb);
                        }
                        if (request.state)
                            setState(request.state);
                        if (request.postcode)
                            setPostcode(request.postcode);
                        if (request.siteName)
                            setSiteName(request.siteName);
                        if (request.tenancyName)
                            setTenancyName(request.tenancyName);
                        if (request.tenancyNumber)
                            setTenancyNumber(request.tenancyNumber);
                        if (request.buildingType)
                            setBuildingType(request.buildingType);
                        if (request.squareMeterage)
                            setSquareMeterage(request.squareMeterage);
                        if (request.scopeOfWorks)
                            setScopeOfWorks(request.scopeOfWorks);
                        if (request.defitType)
                            setDefitType(request.defitType);
                        if (typeof request.siteInspectionAvailable === "boolean")
                            setSiteInspectionAvailable(request.siteInspectionAvailable);
                        if (typeof request.permitRequired === "boolean")
                            setPermitRequired(request.permitRequired);
                        if (request.commencementDate) {
                            const date = new Date(request.commencementDate);
                            setCommencementDate(date.toISOString().split("T")[0]);
                        }
                        if (request.commencementTime)
                            setCommencementTime(request.commencementTime);
                        if (request.completionDate) {
                            const date = new Date(request.completionDate);
                            setCompletionDate(date.toISOString().split("T")[0]);
                        }
                        if (request.completionTime)
                            setCompletionTime(request.completionTime);
                        if (request.projectDuration)
                            setProjectDuration(request.projectDuration);
                        if (request.budgetOption)
                            setBudgetOption(request.budgetOption);
                        if (request.customBudget)
                            setCustomBudget(request.customBudget);
                    }
                    else {
                        // Quote doesn't have request - load from quote data (legacy)
                        if (quote.siteParticulars) {
                            try {
                                const siteData = JSON.parse(quote.siteParticulars);
                                if (siteData.location)
                                    setLocation(siteData.location);
                                if (siteData.suburb) {
                                    setSuburb(siteData.suburb);
                                    setSuburbSearchTerm(siteData.suburb);
                                }
                                if (siteData.state)
                                    setState(siteData.state);
                                if (siteData.postcode)
                                    setPostcode(siteData.postcode);
                                if (siteData.siteName)
                                    setSiteName(siteData.siteName);
                                if (siteData.tenancyName)
                                    setTenancyName(siteData.tenancyName);
                                if (siteData.tenancyNumber)
                                    setTenancyNumber(siteData.tenancyNumber);
                                if (siteData.buildingType)
                                    setBuildingType(siteData.buildingType);
                                if (siteData.squareMeterage)
                                    setSquareMeterage(siteData.squareMeterage);
                                if (siteData.defitType)
                                    setDefitType(siteData.defitType);
                                if (typeof siteData.siteInspectionAvailable === "boolean")
                                    setSiteInspectionAvailable(siteData.siteInspectionAvailable);
                                if (typeof siteData.permitRequired === "boolean")
                                    setPermitRequired(siteData.permitRequired);
                                if (siteData.quoteName)
                                    setQuoteName(siteData.quoteName);
                            }
                            catch (e) {
                                console.error("Error parsing siteParticulars:", e);
                            }
                        }
                        if (quote.title)
                            setQuoteName(quote.title);
                        if (quote.jobDescription)
                            setScopeOfWorks(quote.jobDescription);
                        if (quote.commencementDate) {
                            const date = new Date(quote.commencementDate);
                            setCommencementDate(date.toISOString().split("T")[0]);
                        }
                        if (quote.commencementTime)
                            setCommencementTime(quote.commencementTime);
                        if (quote.completionDate) {
                            const date = new Date(quote.completionDate);
                            setCompletionDate(date.toISOString().split("T")[0]);
                        }
                        if (quote.completionTime)
                            setCompletionTime(quote.completionTime);
                        if (quote.projectDuration)
                            setProjectDuration(quote.projectDuration.toString());
                    }
                }
                // Note: New request creation is handled in a separate useEffect
            }
            catch (err) {
                console.error("Error loading data:", err);
                showError("Failed to load data. Please refresh the page.");
            }
            finally {
                // Loading complete
            }
        };
        if (quoteId || !user?.id) {
            // Only load data if editing a quote or user is not available yet
            loadData();
        }
    }, [quoteId, user]);
    // Handle copied quote - populate form with copied quote data (no POST on init)
    useEffect(() => {
        const loadCopiedQuoteData = async () => {
            if (!copiedQuote || !user?.id || quoteId) {
                // Only handle copied quote if we're creating a new quote (not editing)
                return;
            }
            try {
                // If quote has quoteRequestId, fetch the quote request to populate form
                if (copiedQuote.quoteRequestId) {
                    try {
                        const request = await quoteRequestsService.getQuoteRequest(copiedQuote.quoteRequestId);
                        // Populate form fields from request
                        if (request.location)
                            setLocation(request.location);
                        if (request.suburb) {
                            setSuburb(request.suburb);
                            setSuburbSearchTerm(request.suburb);
                        }
                        if (request.state)
                            setState(request.state);
                        if (request.postcode)
                            setPostcode(request.postcode);
                        if (request.siteName)
                            setSiteName(request.siteName);
                        if (request.tenancyName)
                            setTenancyName(request.tenancyName);
                        if (request.tenancyNumber)
                            setTenancyNumber(request.tenancyNumber);
                        if (request.buildingType)
                            setBuildingType(request.buildingType);
                        if (request.squareMeterage)
                            setSquareMeterage(request.squareMeterage);
                        if (request.scopeOfWorks)
                            setScopeOfWorks(request.scopeOfWorks);
                        if (request.defitType)
                            setDefitType(request.defitType);
                        if (typeof request.siteInspectionAvailable === "boolean")
                            setSiteInspectionAvailable(request.siteInspectionAvailable);
                        if (typeof request.permitRequired === "boolean")
                            setPermitRequired(request.permitRequired);
                        if (request.commencementDate) {
                            const date = new Date(request.commencementDate);
                            setCommencementDate(date.toISOString().split("T")[0]);
                        }
                        if (request.commencementTime)
                            setCommencementTime(request.commencementTime);
                        if (request.completionDate) {
                            const date = new Date(request.completionDate);
                            setCompletionDate(date.toISOString().split("T")[0]);
                        }
                        if (request.completionTime)
                            setCompletionTime(request.completionTime);
                        if (request.projectDuration)
                            setProjectDuration(request.projectDuration);
                        if (request.budgetOption)
                            setBudgetOption(request.budgetOption);
                        if (request.customBudget)
                            setCustomBudget(request.customBudget);
                    }
                    catch (err) {
                        console.error("Error loading quote request for copied quote:", err);
                        // Fall through to extract from quote data
                    }
                }
                // If no quoteRequestId or failed to load request, extract from quote data
                if (!copiedQuote.quoteRequestId || copiedQuote.siteParticulars) {
                    // Extract from siteParticulars if available
                    if (copiedQuote.siteParticulars) {
                        try {
                            const siteData = typeof copiedQuote.siteParticulars === "string"
                                ? JSON.parse(copiedQuote.siteParticulars)
                                : copiedQuote.siteParticulars;
                            if (siteData.location)
                                setLocation(siteData.location);
                            if (siteData.suburb) {
                                setSuburb(siteData.suburb);
                                setSuburbSearchTerm(siteData.suburb);
                            }
                            if (siteData.state)
                                setState(siteData.state);
                            if (siteData.postcode)
                                setPostcode(siteData.postcode);
                            if (siteData.siteName)
                                setSiteName(siteData.siteName);
                            if (siteData.tenancyName)
                                setTenancyName(siteData.tenancyName);
                            if (siteData.tenancyNumber)
                                setTenancyNumber(siteData.tenancyNumber);
                            if (siteData.buildingType)
                                setBuildingType(siteData.buildingType);
                            if (siteData.squareMeterage)
                                setSquareMeterage(siteData.squareMeterage);
                            if (siteData.defitType)
                                setDefitType(siteData.defitType);
                            if (typeof siteData.siteInspectionAvailable === "boolean")
                                setSiteInspectionAvailable(siteData.siteInspectionAvailable);
                            if (typeof siteData.permitRequired === "boolean")
                                setPermitRequired(siteData.permitRequired);
                            if (siteData.quoteName)
                                setQuoteName(siteData.quoteName);
                        }
                        catch (e) {
                            console.error("Error parsing siteParticulars from copied quote:", e);
                        }
                    }
                    // Extract from quote fields directly
                    if (copiedQuote.title)
                        setQuoteName(copiedQuote.title);
                    if (copiedQuote.address && !location)
                        setLocation(copiedQuote.address);
                    if (copiedQuote.jobDescription)
                        setScopeOfWorks(copiedQuote.jobDescription);
                    if (copiedQuote.commencementDate) {
                        const date = new Date(copiedQuote.commencementDate);
                        setCommencementDate(date.toISOString().split("T")[0]);
                    }
                    if (copiedQuote.commencementTime)
                        setCommencementTime(copiedQuote.commencementTime);
                    if (copiedQuote.completionDate) {
                        const date = new Date(copiedQuote.completionDate);
                        setCompletionDate(date.toISOString().split("T")[0]);
                    }
                    if (copiedQuote.completionTime)
                        setCompletionTime(copiedQuote.completionTime);
                    if (copiedQuote.projectDuration)
                        setProjectDuration(copiedQuote.projectDuration.toString());
                }
            }
            catch (err) {
                console.error("Error loading copied quote data:", err);
                showError("Failed to load copied quote data. Please try again.");
            }
        };
        loadCopiedQuoteData();
    }, [copiedQuote, user?.id, quoteId, showError]);
    // Auto-save function with debouncing
    const autoSave = useCallback(async () => {
        if (!currentQuoteRequestId || !user || !user.id || isSavingRef.current)
            return;
        // Clear existing timeout
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }
        // Set new timeout for debouncing (500ms)
        autoSaveTimeoutRef.current = setTimeout(async () => {
            isSavingRef.current = true;
            try {
                await quoteRequestsService.updateQuoteRequest(currentQuoteRequestId, {
                    location,
                    suburb,
                    state,
                    postcode,
                    siteName,
                    tenancyName,
                    tenancyNumber,
                    buildingType,
                    squareMeterage,
                    scopeOfWorks,
                    defitType,
                    siteInspectionAvailable,
                    permitRequired,
                    commencementDate: commencementDate
                        ? new Date(commencementDate).toISOString()
                        : undefined,
                    commencementTime,
                    completionDate: completionDate
                        ? new Date(completionDate).toISOString()
                        : undefined,
                    completionTime,
                    projectDuration,
                    budgetOption: budgetOption || undefined,
                    customBudget,
                });
            }
            catch (err) {
                console.error("Error auto-saving quote request:", err);
                // Don't show error to user for auto-save failures
            }
            finally {
                isSavingRef.current = false;
            }
        }, 500);
    }, [
        currentQuoteRequestId,
        user,
        location,
        suburb,
        state,
        postcode,
        siteName,
        tenancyName,
        tenancyNumber,
        buildingType,
        squareMeterage,
        scopeOfWorks,
        defitType,
        siteInspectionAvailable,
        permitRequired,
        commencementDate,
        commencementTime,
        completionDate,
        completionTime,
        projectDuration,
        budgetOption,
        customBudget,
    ]);
    // Auto-save when form data changes
    useEffect(() => {
        if (currentQuoteRequestId && user && user.id) {
            autoSave();
        }
        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, [autoSave, currentQuoteRequestId, user]);
    const filteredJobDescriptionDocs = useMemo(() => {
        let filtered = pendingDocuments;
        // Apply category filter
        if (jobDescriptionFilter !== "all") {
            filtered = filtered.filter((doc) => doc.category === jobDescriptionFilter);
        }
        // Apply search filter
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter((doc) => doc.name.toLowerCase().includes(searchLower));
        }
        return filtered;
    }, [jobDescriptionFilter, searchTerm, pendingDocuments]);
    // Filter suburbs based on search term (fallback to Matthew Proctor if not in local list)
    useEffect(() => {
        if (suburbSearchTerm.trim()) {
            searchSuburbsAsync(suburbSearchTerm).then((results) => {
                setFilteredSuburbs(results.slice(0, 50));
            });
        }
        else {
            setFilteredSuburbs([]);
        }
    }, [suburbSearchTerm]);
    // Handle outside clicks for suburb dropdown
    useEffect(() => {
        if (!showSuburbDropdown)
            return;
        const container = suburbDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && !container.contains(target)) {
                setShowSuburbDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowSuburbDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showSuburbDropdown]);
    // Suburb handlers
    const handleSuburbSelect = (selectedSuburb) => {
        setSuburbSearchTerm(selectedSuburb.name);
        setSuburb(selectedSuburb.name);
        setState(selectedSuburb.state);
        setPostcode(selectedSuburb.postcode);
        setShowSuburbDropdown(false);
    };
    const handleClearSuburb = () => {
        setSuburbSearchTerm("");
        setSuburb("");
        setState("");
        setPostcode("");
        setFilteredSuburbs([]);
    };
    // Number-only validation handlers
    const handleNumberInput = (e, setValue) => {
        const value = e.target.value;
        // Remove any non-numeric characters
        const numericValue = value.replace(/\D/g, "");
        setValue(numericValue);
    };
    const handleBack = () => {
        // Navigate back - quote request is already saved in backend
        navigate("/client/quotes");
    };
    const handleNext = () => {
        const index = TABS.findIndex((t) => t.id === activeTab);
        if (index >= 0 && index < TABS.length - 1) {
            setActiveTab(TABS[index + 1].id);
        }
    };
    const handleSubmit = async () => {
        if (!user || !user.id) {
            showError("User not authenticated");
            return;
        }
        const userId = user.id;
        // Values are now available from controlled state
        // location, siteName, tenancyName, tenancyNumber, squareMeterage, scopeOfWorks
        // Basic validation
        if (!location.trim()) {
            showError("Please enter an address");
            return;
        }
        if (commencementDate && isWeekend(commencementDate)) {
            showError("Commencement date cannot fall on a weekend.");
            return;
        }
        setSubmitting(true);
        try {
            // Calculate budget
            let estimatedBudget;
            if (budgetOption === "10K") {
                estimatedBudget = 10000;
            }
            else if (budgetOption === "20K") {
                estimatedBudget = 20000;
            }
            else if (budgetOption === "50K") {
                estimatedBudget = 50000;
            }
            else if (budgetOption === "Other" && customBudget) {
                estimatedBudget =
                    parseFloat(customBudget.replace(/[^0-9.]/g, "")) || undefined;
            }
            // Create area info - full structure matching Estimating DEFAULT_AREA_INFO
            // so Quote and Site both have Tenancy Defit (m2) for Estimate display
            const trimmedSquareMeterage = String(squareMeterage ?? "").trim();
            const tenancyDefitValue = trimmedSquareMeterage
                ? parseFloat(trimmedSquareMeterage) || 0
                : 0;
            const areaInfo = [
                { label: "Tenancy Defit (m2)", value: tenancyDefitValue, unit: "m2" },
                { label: "Wall Finish (Plaster) (m2)", value: 0, unit: "m2" },
                { label: "Wall Finish (Brickwork) (m2)", value: 0, unit: "m2" },
                { label: "Shopfront (l/m)", value: 0, unit: "l/m" },
                {
                    label: "Shopfront (under 3.6m) OR of Kiosk Lm (m2)",
                    value: 0,
                    unit: "m2",
                },
            ];
            // Build address
            const addressParts = [location];
            if (suburb)
                addressParts.push(suburb);
            if (state)
                addressParts.push(state);
            if (postcode)
                addressParts.push(postcode);
            const address = addressParts.join(", ");
            // Quote description should ONLY contain Scope of Works
            // Site details are already stored in siteParticulars for Site creation
            // Format dates for API (native date input returns YYYY-MM-DD format)
            const formattedCommencementDate = commencementDate
                ? new Date(commencementDate).toISOString()
                : undefined;
            const formattedCompletionDate = completionDate
                ? new Date(completionDate).toISOString()
                : undefined;
            const commencementTimeFull = commencementTime || undefined;
            const completionTimeFull = completionTime || undefined;
            const quoteRequestPayload = {
                contactId: userId,
                location,
                suburb,
                state,
                postcode,
                siteName,
                tenancyName,
                tenancyNumber,
                buildingType,
                squareMeterage,
                scopeOfWorks,
                defitType,
                siteInspectionAvailable,
                permitRequired,
                commencementDate: formattedCommencementDate,
                commencementTime: commencementTimeFull,
                completionDate: formattedCompletionDate,
                completionTime: completionTimeFull,
                projectDuration,
                budgetOption: budgetOption || undefined,
                customBudget,
            };
            // Ensure we have a quote request (create only on submit, not on page init)
            let quoteRequestId = currentQuoteRequestId;
            if (!quoteRequestId) {
                const newRequest = await quoteRequestsService.createQuoteRequest(quoteRequestPayload);
                quoteRequestId = newRequest.id;
                setCurrentQuoteRequestId(newRequest.id);
            }
            else {
                await quoteRequestsService.updateQuoteRequest(quoteRequestId, quoteRequestPayload);
            }
            // Submit the quote request
            await quoteRequestsService.submitQuoteRequest(quoteRequestId);
            // Fetch contact for company name
            const contactRes = await contactsService.getContact(userId);
            const companyName = contactRes.data?.companyName?.trim() || undefined;
            // Create site entity from Site Particulars
            const siteCreateData = {
                siteName: siteName || location || "New Site",
                tenancyName: tenancyName || undefined,
                tenancyNumber: tenancyNumber || undefined,
                address: location || undefined,
                suburb: suburb || undefined,
                state: state || undefined,
                postcode: postcode || undefined,
                buildingTypes: buildingType ? [buildingType] : undefined,
                companyName,
                areaInfo,
                contactPersonId: userId,
            };
            const newSiteResponse = await sitesService.createSite(siteCreateData);
            const siteId = newSiteResponse.data.id;
            const createData = {
                contactId: userId,
                siteId,
                title: quoteName || location || "New Quote Request",
                address: address || undefined,
                description: scopeOfWorks || undefined,
                status: "draft",
                defitType,
                commencementDate: formattedCommencementDate,
                commencementTime: commencementTimeFull,
                completionDate: formattedCompletionDate,
                completionTime: completionTimeFull,
                projectDuration: projectDuration
                    ? parseInt(projectDuration, 10)
                    : undefined,
                areaInfo,
                totalAmount: estimatedBudget,
                attentionContactId: userId,
                quoteRequestId: quoteRequestId,
                siteParticulars: JSON.stringify({
                    location,
                    suburb,
                    state,
                    postcode,
                    siteName,
                    tenancyName,
                    tenancyNumber,
                    buildingType,
                    squareMeterage,
                    siteInspectionAvailable,
                    permitRequired,
                    defitType,
                    quoteName,
                }),
                jobDescription: scopeOfWorks || undefined,
            };
            const quoteResponse = await quotesService.createQuote(createData);
            // Upload pending documents to quote
            if (pendingDocuments.length > 0) {
                setIsUploadingToBlob(true);
                try {
                    for (const pendingDoc of pendingDocuments) {
                        await documentsService.uploadQuoteDocument(pendingDoc.file, {
                            quoteId: quoteResponse.data.id,
                            name: pendingDoc.name.split(".")[0] || pendingDoc.name,
                            quoteCategory: pendingDoc.category,
                        });
                    }
                    setPendingDocuments([]);
                }
                catch (uploadError) {
                    console.error("Error uploading documents:", uploadError);
                    showError("Quote created successfully, but there was an error uploading some documents. You can upload them later from the quote details.");
                }
                finally {
                    setIsUploadingToBlob(false);
                }
            }
            showSuccess("Quote request submitted successfully");
            navigate("/client/quotes");
        }
        catch (err) {
            console.error("Error creating quote:", err);
            showError("Failed to submit quote request. Please try again.");
        }
        finally {
            setSubmitting(false);
        }
    };
    const handleOpenUploadModal = () => setIsUploadModalOpen(true);
    const handleCloseUploadModal = () => setIsUploadModalOpen(false);
    const handleUploadSave = async (files, modalCategory) => {
        if (!user || !user.id) {
            showError("User not authenticated");
            return;
        }
        setIsSavingDocuments(true);
        try {
            // Map modal categories to document categories
            const categoryMap = {
                Permits: "permits",
                Plans: "plans",
                Other: "other",
            };
            const documentCategory = categoryMap[modalCategory] || "other";
            // Create pending documents
            const newPendingDocuments = files.map((fileInfo) => {
                if (!fileInfo.file) {
                    throw new Error(`File object missing for ${fileInfo.name}`);
                }
                return {
                    id: `pending-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    name: fileInfo.name,
                    file: fileInfo.file,
                    category: documentCategory,
                    date: new Date().toISOString(),
                    uploadedBy: user.name || user.email || "Current User",
                };
            });
            // Add to pending documents state
            setPendingDocuments((prev) => [...prev, ...newPendingDocuments]);
            // Close modal and show success
            setIsUploadModalOpen(false);
            showSuccess(`${files.length} document${files.length > 1 ? "s" : ""} saved successfully`);
        }
        catch (err) {
            console.error("Error saving documents:", err);
            const errorMessage = err?.message || "Failed to save documents";
            showError(`Failed to save documents: ${errorMessage}`);
            // Don't close modal on error so user can retry
        }
        finally {
            setIsSavingDocuments(false);
        }
    };
    const handleDownloadJobDocument = (doc) => {
        // For pending documents, create a download link
        const pendingDoc = pendingDocuments.find((d) => d.id === doc.id);
        if (pendingDoc) {
            const url = URL.createObjectURL(pendingDoc.file);
            const a = document.createElement("a");
            a.href = url;
            a.download = pendingDoc.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
    };
    const handleDeleteJobDocument = (docId) => {
        setPendingDocuments((prev) => prev.filter((doc) => doc.id !== docId));
        showSuccess("Document removed successfully");
    };
    return (_jsxs("div", { className: styles.page, children: [_jsx("header", { className: styles.pageHeader, children: _jsxs("div", { children: [_jsx("button", { onClick: handleBack, className: styles.backButton, children: "\u2190 Back to Quotes" }), _jsx("h1", { className: styles.title, children: "REQUEST A QUOTE" })] }) }), _jsx("nav", { className: styles.navTabs, children: _jsx("div", { className: styles.tabList, children: TABS.map((tab) => (_jsx("button", { className: `${styles.tabButton} ${activeTab === tab.id ? styles.active : ""}`, onClick: () => setActiveTab(tab.id), children: tab.label }, tab.id))) }) }), _jsxs("div", { className: styles.content, children: [activeTab === "site_particulars" && (_jsx("div", { className: styles.tabContent, children: _jsxs("div", { className: styles.twoColumnGrid, children: [_jsxs("div", { className: styles.paper, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Address" }), _jsx("input", { type: "text", className: "form-input", value: location, onChange: (e) => setLocation(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Suburb" }), _jsxs("div", { ref: suburbDropdownRef, className: styles.contactDropdownWrapper, "data-dropdown-container": true, children: [_jsxs("div", { className: styles.contactInputWrapper, children: [_jsx("input", { type: "text", className: "form-input", value: suburbSearchTerm, onChange: (e) => {
                                                                        const newValue = e.target.value;
                                                                        setSuburbSearchTerm(newValue);
                                                                        setShowSuburbDropdown(true);
                                                                        setSuburb(newValue);
                                                                    }, onFocus: () => setShowSuburbDropdown(true), placeholder: "Search suburbs..." }), suburbSearchTerm && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClearSuburb, children: "\u00D7" }))] }), showSuburbDropdown && (_jsx("div", { className: styles.contactDropdownMenu, children: filteredSuburbs.length === 0 && suburbSearchTerm ? (_jsx("div", { className: styles.dropdownEmpty, children: "No suburbs found" })) : filteredSuburbs.length === 0 ? (_jsx("div", { className: styles.dropdownEmpty, children: "Start typing to search suburbs..." })) : (filteredSuburbs.map((suburbOption, index) => (_jsx("button", { type: "button", className: `${styles.contactOption} ${suburb === suburbOption.name
                                                                    ? styles.selected
                                                                    : ""}`, onClick: () => handleSuburbSelect(suburbOption), children: _jsxs("span", { className: styles.contactName, children: [suburbOption.name, ", ", suburbOption.state, " ", suburbOption.postcode] }) }, `${suburbOption.name}-${suburbOption.state}-${index}`)))) }))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "State" }), _jsxs("select", { className: "form-select", value: state, onChange: (e) => setState(e.target.value), children: [_jsx("option", { value: "", children: "Select State" }), AUSTRALIAN_STATES.map((s) => (_jsx("option", { value: s.value, children: s.label }, s.value)))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Postcode" }), _jsx("input", { type: "text", className: "form-input", value: postcode, onChange: (e) => handleNumberInput(e, setPostcode), maxLength: 4 })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Site Name" }), _jsx("input", { type: "text", className: "form-input", value: siteName, onChange: (e) => setSiteName(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Tenancy Name" }), _jsx("input", { type: "text", className: "form-input", value: tenancyName, onChange: (e) => setTenancyName(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Tenancy Number" }), _jsx("input", { type: "text", className: "form-input", value: tenancyNumber, onChange: (e) => setTenancyNumber(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Building Type" }), _jsxs("select", { className: "form-select", value: buildingType, onChange: (e) => setBuildingType(e.target.value), children: [_jsx("option", { value: "", children: "Select Building Type" }), BUILDING_TYPES.map((type) => (_jsx("option", { value: type.value, children: type.label }, type.value)))] })] })] }), _jsxs("div", { className: styles.paper, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Square Meterage" }), _jsx("input", { type: "text", className: "form-input", value: squareMeterage, onChange: (e) => handleNumberInput(e, setSquareMeterage), onKeyPress: (e) => {
                                                        if (!/^\d$/.test(e.key) &&
                                                            e.key !== "Backspace" &&
                                                            e.key !== "Delete" &&
                                                            e.key !== "Tab" &&
                                                            e.key !== "Enter") {
                                                            e.preventDefault();
                                                        }
                                                    } })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Site Inspection Available" }), _jsx("div", { className: styles.toggleWrapper, children: _jsxs("button", { className: `${styles.toggle} ${siteInspectionAvailable ? styles.toggleActive : ""}`, onClick: () => setSiteInspectionAvailable(!siteInspectionAvailable), children: [_jsx("span", { className: styles.toggleLabel, children: siteInspectionAvailable ? "YES" : "NO" }), _jsx("span", { className: styles.toggleSwitch })] }) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Building/Demolition Permit Required" }), _jsx("div", { className: styles.toggleWrapper, children: _jsxs("button", { className: `${styles.toggle} ${permitRequired ? styles.toggleActive : ""}`, onClick: () => setPermitRequired(!permitRequired), children: [_jsx("span", { className: styles.toggleLabel, children: permitRequired ? "YES" : "NO" }), _jsx("span", { className: styles.toggleSwitch })] }) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Type" }), _jsxs("div", { className: styles.defitToggleWrapper, children: [_jsx("span", { className: `${styles.defitOption} ${defitType === "full" ? styles.defitOptionActive : ""}`, children: "Full Defit" }), _jsx("button", { className: `${styles.defitToggle} ${defitType === "partial" ? styles.defitToggleActive : ""}`, onClick: () => setDefitType(defitType === "full" ? "partial" : "full"), children: _jsx("span", { className: styles.defitToggleSwitch }) }), _jsx("span", { className: `${styles.defitOption} ${defitType === "partial" ? styles.defitOptionActive : ""}`, children: "Partial Defit" })] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Quote Name" }), _jsx("input", { type: "text", className: "form-input", value: quoteName, onChange: (e) => setQuoteName(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroupVertical} form-group-col`, children: [_jsx("label", { className: "form-label", children: "Scope of Works" }), _jsx("textarea", { className: "form-textarea", rows: 10, value: scopeOfWorks, onChange: (e) => setScopeOfWorks(e.target.value) })] })] })] }) })), activeTab === "job_description" && (_jsx("div", { className: styles.tabContent, children: _jsxs("div", { className: styles.twoColumnGrid, children: [_jsxs("div", { className: styles.paper, children: [_jsx("div", { className: styles.dateTimeSection, children: _jsxs("div", { className: styles.dateTimeRow, children: [_jsx("div", { className: styles.dateTimeBlock, children: _jsxs("div", { className: styles.dateTimeInputs, children: [_jsx("label", { className: `form-label ${styles.dateTimeLabel}`, children: "Commencement Date and Time" }), _jsx("div", { className: styles.dateInput, children: _jsx(CommencementDateInput, { className: "form-input", value: commencementDate, onChange: setCommencementDate }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: "form-input", value: commencementTime, onChange: (e) => setCommencementTime(e.target.value) }) })] }) }), _jsx("div", { className: styles.dateTimeBlock, children: _jsxs("div", { className: styles.dateTimeInputs, children: [_jsx("label", { className: `form-label ${styles.dateTimeLabel}`, children: "Completion Date and Time" }), _jsx("div", { className: styles.dateInput, children: _jsx(DateInput, { className: "form-input", value: completionDate, onChange: setCompletionDate, onInvalid: () => showError("Please enter a valid date.") }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: "form-input", value: completionTime, onChange: (e) => setCompletionTime(e.target.value) }) })] }) })] }) }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Project Duration (days)" }), _jsx("input", { type: "text", className: styles.durationInput, value: projectDuration, onChange: (e) => setProjectDuration(e.target.value) })] })] }), _jsxs("div", { className: styles.paper, children: [_jsxs("div", { className: `${styles.formGroupVertical} form-group-col`, children: [_jsx("label", { className: "form-label", children: "Estimated Budget" }), _jsx("div", { className: styles.budgetOptions, children: ["10K", "20K", "50K", "Other"].map((option) => (_jsx("button", { type: "button", className: `${styles.budgetButton} ${budgetOption === option
                                                            ? styles.budgetButtonActive
                                                            : ""}`, onClick: () => setBudgetOption(option), children: option }, option))) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Enter Budget" }), _jsx("input", { type: "text", className: "form-input", value: customBudget, onChange: (e) => setCustomBudget(e.target.value) })] })] })] }) })), activeTab === "documents" && (_jsx("div", { className: styles.tabContent, children: _jsxs("div", { className: styles.jobDocSection, children: [_jsxs("div", { className: styles.jobDocHeader, children: [_jsx("div", { className: styles.jobDocFilterTabs, children: JOB_DESCRIPTION_FILTERS.map((filter) => (_jsx("button", { type: "button", className: `${styles.jobDocFilterTab} ${jobDescriptionFilter === filter.id
                                                    ? styles.jobDocFilterTabActive
                                                    : ""}`, onClick: () => setJobDescriptionFilter(filter.id), children: _jsx("span", { children: filter.label }) }, filter.id))) }), _jsxs("div", { className: styles.jobDocHeaderActions, children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsxs("svg", { className: styles.searchIcon, width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("path", { d: "m21 21-4.35-4.35" })] }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) })] }), _jsx("button", { type: "button", className: styles.addDocsButton, onClick: handleOpenUploadModal, children: "Add Documents" })] })] }), _jsx("div", { className: styles.jobDocTableWrapper, children: _jsxs("table", { className: styles.jobDocTable, children: [_jsx("thead", { children: _jsxs("tr", { className: styles.jobDocTableHeader, children: [_jsx("th", { children: "Name" }), _jsx("th", { children: "Category" }), _jsx("th", { children: "Date" }), _jsx("th", { children: "Uploaded By" }), _jsx("th", { children: "Actions" })] }) }), _jsx("tbody", { children: filteredJobDescriptionDocs.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: 5, className: styles.jobDocEmptyState, children: "No documents stored yet" }) })) : (filteredJobDescriptionDocs.map((doc) => (_jsxs("tr", { className: styles.jobDocTableRow, children: [_jsx("td", { className: styles.jobDocTableCell, children: _jsx("div", { className: styles.documentName, children: _jsxs("div", { className: styles.documentInfo, children: [_jsx("span", { className: styles.name, children: doc.name }), _jsx("span", { className: styles.meta, children: getJobDescriptionFilterLabel(doc.category) })] }) }) }), _jsx("td", { className: styles.jobDocTableCell, children: _jsx("span", { className: styles.categoryLabel, children: getJobDescriptionFilterLabel(doc.category) }) }), _jsx("td", { className: styles.jobDocTableCell, children: _jsx("span", { className: styles.date, children: formatDocDate(doc.date) }) }), _jsx("td", { className: styles.jobDocTableCell, children: _jsx("div", { className: styles.uploadedBy, children: _jsxs("div", { className: styles.userInfo, children: [_jsx("div", { className: styles.avatarPlaceholder, children: (doc.uploadedBy || "U").charAt(0) }), _jsx("span", { className: styles.userName, children: doc.uploadedBy })] }) }) }), _jsx("td", { className: styles.jobDocTableCell, children: _jsxs("div", { className: styles.jobDocActions, children: [_jsx("button", { className: styles.jobDocActionButton, onClick: () => handleDownloadJobDocument(doc), title: "Download", children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M12 5v10" }), _jsx("path", { d: "M8 15l4 4 4-4" }), _jsx("path", { d: "M4 19h16" })] }) }), _jsx("button", { className: styles.jobDocDeleteButton, onClick: () => handleDeleteJobDocument(doc.id), title: "Delete", children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" }), _jsx("line", { x1: "6", y1: "18", x2: "18", y2: "6" })] }) })] }) })] }, doc.id)))) })] }) })] }) }))] }), _jsx(UploadDocumentsModal, { isOpen: isUploadModalOpen, onClose: handleCloseUploadModal, onSave: handleUploadSave, categories: ["Permits", "Plans", "Other"], isUploading: isSavingDocuments, uploadButtonText: "Saving..." }), _jsx("div", { className: styles.footer, children: activeTab === "documents" ? (_jsx("button", { className: styles.nextButton, onClick: handleSubmit, disabled: submitting || isUploadingToBlob, children: isUploadingToBlob
                        ? "Uploading Documents..."
                        : submitting
                            ? "Submitting..."
                            : "Submit Quote Request" })) : (_jsx("button", { className: styles.nextButton, onClick: handleNext, children: "Next" })) })] }));
};
